/**
 * Compare-at (strikethrough) configuration — mirrors PHP StrikethroughPricing.
 */

const MODE_NONE = 'none';
const MODE_FIXED = 'fixed_cents';
const MODE_PERCENT = 'percent';
export const MODE_ABSOLUTE_CENTS = 'absolute_cents';
const OVERRIDE_INHERIT = 'inherit';

/**
 * ACF select / radio in the block editor is often `{ value, label }`; REST/meta can be a string.
 *
 * @param {unknown} raw
 * @param {string} defaultVal
 * @returns {string}
 */
function acfChoiceString(raw, defaultVal) {
  if (raw == null || raw === '' || raw === false) {
    return defaultVal;
  }
  if (typeof raw === 'object' && raw !== null && 'value' in raw) {
    const v = raw.value;
    if (v == null || v === '') {
      return defaultVal;
    }
    return String(v);
  }
  return String(raw);
}

/**
 * @returns {{ mode: string, fixed_cents: number|null, percent: number|null }}
 */
export function getStrikethroughSiteDefaults() {
  const raw =
    window.currencyData?.strikethroughSiteDefaults ||
    window.FHS?.strikethroughSiteDefaults;
  if (!raw || typeof raw !== 'object') {
    return { mode: MODE_NONE, fixed_cents: null, percent: null };
  }
  return {
    mode: acfChoiceString(raw.mode, MODE_NONE),
    fixed_cents:
      raw.fixed_cents != null && raw.fixed_cents !== ''
        ? parseInt(raw.fixed_cents, 10)
        : null,
    percent:
      raw.percent != null && raw.percent !== ''
        ? parseFloat(String(raw.percent).replace(',', '.'))
        : null,
  };
}

/**
 * @param {string} [override]
 * @param {string|number|null} itemFixed
 * @param {string|number|null} itemPct
 * @param {string|number|null} itemAbsoluteCents item-only fixed compare-at total (cents)
 * @returns {{ mode: string, fixed_cents: number, percent: number, absolute_cents?: number }|null}
 */
export function resolveStrikethroughConfig(
  override,
  itemFixed,
  itemPct,
  itemAbsoluteCents,
) {
  const o = acfChoiceString(override, OVERRIDE_INHERIT);
  const site = getStrikethroughSiteDefaults();

  if (o === MODE_NONE) {
    return null;
  }

  if (o === MODE_ABSOLUTE_CENTS) {
    const parsed =
      itemAbsoluteCents != null && itemAbsoluteCents !== ''
        ? parseInt(itemAbsoluteCents, 10)
        : NaN;
    if (isNaN(parsed) || parsed <= 0) {
      return null;
    }
    return {
      mode: MODE_ABSOLUTE_CENTS,
      absolute_cents: parsed,
      fixed_cents: 0,
      percent: 0,
    };
  }

  let mode = site.mode || MODE_NONE;
  let fixedCents = site.fixed_cents;
  let pct = site.percent;

  if (o === OVERRIDE_INHERIT) {
    mode = site.mode || MODE_NONE;
    fixedCents = site.fixed_cents;
    pct = site.percent;
  } else if (o === MODE_FIXED) {
    mode = MODE_FIXED;
    const parsed =
      itemFixed != null && itemFixed !== ''
        ? parseInt(itemFixed, 10)
        : NaN;
    fixedCents = !isNaN(parsed) ? parsed : site.fixed_cents;
    pct = site.percent;
  } else if (o === MODE_PERCENT) {
    mode = MODE_PERCENT;
    const parsedP =
      itemPct != null && itemPct !== ''
        ? parseFloat(String(itemPct).replace(',', '.'))
        : NaN;
    pct = !isNaN(parsedP) ? parsedP : site.percent;
    fixedCents = site.fixed_cents;
  } else {
    return null;
  }

  if (mode === MODE_NONE || !mode) {
    return null;
  }

  if (mode === MODE_FIXED) {
    if (fixedCents == null || fixedCents <= 0) {
      return null;
    }
    return { mode: MODE_FIXED, fixed_cents: fixedCents, percent: 0 };
  }

  if (mode === MODE_PERCENT) {
    if (pct == null || pct <= 0 || isNaN(pct)) {
      return null;
    }
    return { mode: MODE_PERCENT, fixed_cents: 0, percent: pct };
  }

  return null;
}

/**
 * @param {number} baseCents
 * @param {{ mode: string, fixed_cents: number, percent: number }} config
 * @returns {number}
 */
export function computeCompareAtCents(baseCents, config) {
  if (!config) {
    return 0;
  }
  if (config.mode === MODE_ABSOLUTE_CENTS) {
    return parseInt(String(config.absolute_cents), 10) || 0;
  }
  if (baseCents <= 0) {
    return 0;
  }
  if (config.mode === MODE_FIXED) {
    return baseCents + (parseInt(config.fixed_cents, 10) || 0);
  }
  if (config.mode === MODE_PERCENT) {
    const p = parseFloat(String(config.percent), 10);
    return Math.round(baseCents * (1 + p / 100));
  }
  return 0;
}

/**
 * @param {number|null} lowCents
 * @param {number} compareCents
 * @param {{ mode: string }} config
 */
export function shouldDisplayCompareAt(lowCents, compareCents, config) {
  if (!config || compareCents == null || compareCents <= 0) {
    return false;
  }
  if (config.mode === MODE_ABSOLUTE_CENTS) {
    return compareCents !== lowCents;
  }
  if (lowCents == null || lowCents <= 0) {
    return false;
  }
  return compareCents > lowCents;
}

/**
 * @param {string|number|null} lowPriceValue
 * @returns {number|null}
 */
export function parseLowPriceCents(lowPriceValue) {
  if (lowPriceValue == null || lowPriceValue === '') {
    return null;
  }
  const s = String(lowPriceValue).trim().toLowerCase();
  if (s === 'free') {
    return null;
  }
  const cents = parseInt(s, 10);
  if (isNaN(cents) || cents <= 0) {
    return null;
  }
  return cents;
}
